import React, { useState, useRef, useEffect } from 'react';
import { Upload, RotateCcw, Trash, ImagePlus, BadgeInfo, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import './PetCustomizer.scss';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { useSelector, useDispatch } from 'react-redux';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import useFileHandlers from '../../hooks/useFileHandlers';
import { useAffiliate } from '../../hooks/useAffiliate';
import Button from '../ui/Button';
import FramesModal from './FramesModal';
import { Tooltip } from 'react-tooltip';
import { toast } from 'react-toastify';
import {
  drawCanvas
} from './functions/canvasRenderer';
import {
  loadImagesFromDB
} from './functions/loadImagesFromIndexedDB';
import {
  handlePointerDown,
  handlePointerMove,
  handlePointerUp
} from './functions/pointerHandlers';
import {
  downloadCanvas
} from './functions/downloadCanvas';
import {
  saveCanvas
} from './functions/saveCanvas';
import { CANVAS_HEIGHT, CANVAS_WIDTH, CUSTOMIZER_MAX_IMAGES_COUNT } from '../../config/config';
import upload from '/icons/upload.svg';
import dog_icon from '/icons/dog-icon.svg';
import cat_icon from '/icons/cat-icon.svg';
import {
  duplicateImageAsync,
  setSelectedFrame,
  setSelectedFrameCategory,
  setIncludePetName,
  setPetName,
  setPetType,
  selectUploadedImageIds,
  removeImage,
  moveImage,
  rotateImage,
  scaleImage,
  resetCustomizer
} from '../../store/customizerSlice';
import { deleteImageFromDB, resetIndexedDB } from '../../utils/indexedDB';
import { labelRestorationUpdate } from './functions/labelRestorationUpdate';
import { getFrameByName } from './functions/getSelectedFrame';

const RestoreOrderLabel = ({ orderItem, token }) => {
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const { affiliate } = useAffiliate();
  const { handleFileSelect, handleDrop, processImage } = useFileHandlers();

  const canvasWrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const frameSelectionRef = useRef(null);
  const innerFrameContentRef = useRef(null);
  const framesSwiperRef = useRef(null);
  const imageCache = useRef(new Map());
  const touchesRef = useRef({
    initialDistance: null,
    initialScale: null,
  });

  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [staticBg, setStaticBg] = useState(null);
  const [frameImage, setFrameImage] = useState(null);
  const [imageElements, setImageElements] = useState(new Map());
  const [frameSectionHeight, setFrameSectionHeight] = useState(null);
  const [isFramesModalActive, setFramesModalActive] = useState(false);
  const [isFrameSelectionExpanded, setIsFrameSelectionExpanded] = useState(true);
  const [runOnRender, setRunOnRender] = useState(false);
  const includePetName = useSelector((state) => state.customizer.includePetName);
  const [submitError, setSubmitError] = useState([]);
  const [submitInProgress, setSubmitInProgress] = useState(false);

  const petName = useSelector((state) => state.customizer.petName);
  const images = useSelector((state) => state.customizer.images);
  const frames = useSelector((state) => state.customizer.frames);
  const framesCategories = useSelector((state) => state.customizer.framesCategories);
  const selectedFrame = useSelector((state) => state.customizer.selectedFrame);
  const selectedFrameCategory = useSelector((state) => state.customizer.selectedFrameCategory);
  const uploadedImageIds = useSelector(selectUploadedImageIds);

  const filteredFrames = frames.filter((f) => f.category === selectedFrameCategory);

  // initial reset
  useEffect(() => {
    resetIndexedDB("uploadedImages", []);
    resetIndexedDB("resultImages", []);
    resetIndexedDB("onProductImages", []);
    dispatch(resetCustomizer());
  }, []);

  // Get original image
  const hasPreloaded = useRef(false);

  useEffect(() => {
    if (hasPreloaded.current) return;
    hasPreloaded.current = true;

    const preloadOriginalImage = async () => {
      if (orderItem?.item?.original_image) {
        try {
          const response = await fetch(orderItem.item.original_image);
          const blob = await response.blob();
          const fileName = `preloaded_${Date.now()}.png`;
          const file = new File([blob], fileName, { type: blob.type });

          await processImage(file, false, orderItem.item.image_metadata || null);
        } catch (err) {
          console.error("Failed to preload original image:", err);
        }
      }
    };

    preloadOriginalImage();
  }, [orderItem]);


  // Get pet name
  useEffect(() => {
    if (orderItem) {
      dispatch(setIncludePetName(true));
      dispatch(setPetName(orderItem.item.pet_name || ''));
    }
  }, [orderItem, dispatch]);


  // Get selected template
  useEffect(() => {
    if (orderItem) {
      const detectedFrame = getFrameByName(frames, orderItem.item.label_template);
      if (detectedFrame) {
        dispatch(setSelectedFrame(detectedFrame));
      }
    }
  }, [orderItem, frames, dispatch]);


  useEffect(() => {
    if (framesSwiperRef.current) {
      framesSwiperRef.current.slideTo(0, 0);
    }
  }, [selectedFrameCategory]);

  useEffect(() => {
    if (!runOnRender) {
      setRunOnRender(true);
      return;
    }
    if (!frameSelectionRef.current || !innerFrameContentRef.current) return;
    requestAnimationFrame(() => {
      const heightToSet = isFrameSelectionExpanded
        ? `${innerFrameContentRef.current.offsetHeight + 10}px`
        : "0px";
      setFrameSectionHeight(heightToSet);
    });
  }, [filteredFrames.length, selectedFrameCategory, isFrameSelectionExpanded]);

  useEffect(() => {
    if (images.length > 0) {
      const lastImage = images[images.length - 1];
      if (selectedImageIndex === null || images[selectedImageIndex]?.id !== lastImage.id) {
        setSelectedImageIndex(images.length - 1);
      }
    }
  }, [images.length]);

  useEffect(() => {
    if (staticBg) {
      setSelectedImageIndex((prev) => prev);
    }
  }, [staticBg]);

  useEffect(() => {
    const img = new Image();
    img.src = "/images/background-grey.png";
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setStaticBg(img);
    };
  }, []);

  useEffect(() => {
    document.fonts.ready.then(() => {
      setTimeout(() => {
        setPetName((prev) => prev + " ");
        setTimeout(() => setPetName((prev) => prev.trim()), 10);
      }, 50);
    });
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = selectedFrame.url;
    img.onload = () => setFrameImage(img);
  }, [selectedFrame]);

  useEffect(() => {
    drawCanvas({
      canvas: canvasRef.current,
      images,
      staticBg,
      frameImage,
      imageCache,
      selectedFrame,
      includePetName,
      petName,
      isDragging,
    });
  }, [images, frameImage, staticBg, imageElements, selectedFrame, petName, includePetName, isDragging]);

  useEffect(() => {
    const load = async () => {
      if (images.length === 0) return;
      const newImages = await loadImagesFromDB(images, imageCache);
      if (newImages.size > 0) {
        setImageElements((prev) => new Map([...prev, ...newImages]));
      }
    };
    load();
  }, [images.length]);

  useEffect(() => {
    const disableScroll = (e) => {
      if (e.target.closest('.customizer-outer-container')) {
        e.preventDefault();
      }
    };

    if (isDragging) {
      document.body.style.overflow = "hidden";
      document.addEventListener("touchmove", disableScroll, { passive: false });
    } else {
      document.body.style.overflow = "";
      document.removeEventListener("touchmove", disableScroll);
    }

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("touchmove", disableScroll);
    };
  }, [isDragging]);

  const pointerDownHandler = (e) => handlePointerDown(e, canvasRef, images, setSelectedImageIndex, setDragStart, setIsDragging, CANVAS_WIDTH, CANVAS_HEIGHT, touchesRef);
  const pointerMoveHandler = (e) => handlePointerMove(e, isDragging, selectedImageIndex, canvasRef, images, dragStart, dispatch, moveImage, CANVAS_WIDTH, CANVAS_HEIGHT, touchesRef);
  const pointerUpHandler = () => handlePointerUp(setDragStart, setIsDragging, touchesRef);

  const rotateImageRight = () => {
    if (selectedImageIndex !== null) {
      dispatch(rotateImage({ id: images[selectedImageIndex].id, angle: 15 }));
    }
  };

  const rotateImageLeft = () => {
    if (selectedImageIndex !== null) {
      dispatch(rotateImage({ id: images[selectedImageIndex].id, angle: -15 }));
    }
  };

  // const duplicateImageHandler = (id) => dispatch(duplicateImageAsync(id));

  const deleteImageHandler = () => {
    if (selectedImageIndex !== null) {
      dispatch(removeImage(images[selectedImageIndex].id));
      deleteImageFromDB("uploadedImages", images[selectedImageIndex].id);
      setSelectedImageIndex(null);
    }
  };

  const scaleImageHandler = (newScale) => {
    if (selectedImageIndex !== null) {
      dispatch(scaleImage({ id: images[selectedImageIndex].id, scale: newScale }));
    }
  };


  const handleLabelSubmit = async () => {
    setSubmitError([]);
    setSubmitInProgress(true);
    setStaticBg(null);

    const resultImageId = Date.now().toString();
    setTimeout(async () => {
      const { success, error } = await labelRestorationUpdate({
        canvasRef,
        token,
        orderId: orderItem.item.id,
        uploadedImageId: uploadedImageIds[0],
        resultImageId,
        petName: petName || null,
        labelTemplate: selectedFrame.name,
        image: images[selectedImageIndex]
      });

      if (!success) {
        setSubmitInProgress(false);
        setSubmitError(error);
        return;
      }

      navigate("/");
      setSubmitInProgress(false);
      toast.success("Label successfully updated!", {
        autoClose: 6000,
      });
    }, 50);
  };

  return (
    <div className={`full-width customizer-outer-container flex justify-center ${affiliate ? 'affiliate-margin' : ''}`}>
      <div className="bg-white rounded-20 box-shadow contained-width customizer-container overflow-hidden">
        <div className="customizer-container-inner p-5 md:p-10 gap-5 md:gap-10">

          {affiliate && (
            <img
              src={affiliate.logo}
              alt={`${affiliate.name} logo`}
              className="affiliate-logo"
            />
          )}

          {/* Controls container */}
          <div className="customizer-controls-container gap-2 md:gap-5">
            {(images?.length > 0 && images?.length < CUSTOMIZER_MAX_IMAGES_COUNT && selectedImageIndex === null) && (
              <>
                <div className="hint "> <BadgeInfo size={19} /> Click the image to edit it</div>
                <button
                  onClick={() => document.getElementById('fileInput').click()}
                  className="upload-btn-outlined"
                >
                  <ImagePlus size={19} /> Upload New Image
                </button>
              </>
            )}

            {images.length > 0 && (
              <div className="flex items-center gap-2 customizer-controls customizer-image-controls" data-ignore-image-deselect>
                <div className='flex flex-col gap-2'>
                  <label className="block  font-medium text-gray-700">
                    Image Controls
                  </label>

                  <div>
                    <button
                      onClick={rotateImageLeft}
                      className="p-3 hover:bg-gray-100 rounded"
                      data-tooltip-id="rotate-left"
                    >
                      <RotateCcw size={20} />
                    </button>
                    <Tooltip className="controls-tooltip" opacity="1" id="rotate-left" content="Rotate Left" place="top" />

                    <button
                      onClick={rotateImageRight}
                      className="p-3 hover:bg-gray-100 rounded"
                      data-tooltip-id="rotate-right"
                    >
                      <RotateCcw size={20} className="transform scale-x-[-1]" />
                    </button>
                    <Tooltip className="controls-tooltip" opacity="1" id="rotate-right" content="Rotate Right" place="top" />

                    <button
                      onClick={deleteImageHandler}
                      className="p-3 hover:bg-gray-100 rounded"
                      data-tooltip-id="delete"
                    >
                      <Trash size={20} />
                    </button>
                    <Tooltip className="controls-tooltip" opacity="1" id="delete" content="Delete Image" place="top" />

                  </div>
                </div>
                {selectedImageIndex !== null && images[selectedImageIndex] && (
                  <div className="mt-4 slider-input">
                    <div
                      className="tooltip"
                      style={{
                        left: `${(
                          (images[selectedImageIndex].scale - 0.4) /
                          (images[selectedImageIndex].maxScale - 0.4)
                        ) * 100}%`,
                      }}
                    >
                      {`${Math.round(images[selectedImageIndex].width * images[selectedImageIndex].scale)} x ${Math.round(
                        images[selectedImageIndex].height * images[selectedImageIndex].scale
                      )}px`}
                    </div>
                    <input
                      type="range"
                      min={0.5}
                      max={images[selectedImageIndex].maxScale}
                      step="0.1"
                      value={images[selectedImageIndex].scale}
                      onChange={(e) =>
                        scaleImageHandler(parseFloat(e.target.value))
                      }
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Pet name */}
            <div className="customizer-controls pet-name-container">
              <div className='flex flex-col gap-2 w-full items-center' data-ignore-image-deselect>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={includePetName}
                    onChange={() => dispatch(setIncludePetName(!includePetName))}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <span className="font-medium text-gray-700 cursor-pointer">Include Pet Name on Label</span>
                </label>

                {includePetName && (
                  <div className="flex flex-col gap-2 w-full items-center">
                    <input
                      value={petName}
                      type="text"
                      maxLength="20"
                      placeholder="Pet Name"
                      onChange={(e) => dispatch(setPetName(e.target.value))}
                      className="pet-name-input"
                    />
                  </div>
                )}
              </div>
            </div>


            {/* Frame select */}
            <div className="customizer-controls">
              <div
                className="flex items-center justify-center gap-2 w-full cursor-pointer"
                onClick={() => setIsFrameSelectionExpanded(!isFrameSelectionExpanded)}
                data-ignore-image-deselect
              >
                <label className="block font-medium text-gray-700">Choose Your Background</label>
                {isFrameSelectionExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>

              <div
                ref={frameSelectionRef}
                className="collapsible-content"
                style={{
                  height:
                    frameSectionHeight !== null
                      ? frameSectionHeight
                      : isFrameSelectionExpanded
                        ? `${frameSelectionRef.current?.scrollHeight || 0}px`
                        : "0px"
                }}
                data-ignore-image-deselect
              >
                <div ref={innerFrameContentRef}>
                  {/* Category Filters */}
                  <div className="flex gap-2 justify-center flex-wrap mt-2">
                    {framesCategories.map((category) => (
                      <button
                        key={category}
                        onClick={() => dispatch(setSelectedFrameCategory(category))}
                        className={`frame-category px-2 py-1 rounded-10 transition ${selectedFrameCategory === category ? "selected" : ""
                          }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>

                  <div className="flex flex-col items-center justify-center gap-4 mt-3">
                    <Swiper
                      modules={[Pagination]}
                      onSwiper={(swiper) => {
                        framesSwiperRef.current = swiper;
                      }}
                      pagination={{ el: ".frames-pagination", clickable: true }}
                      spaceBetween={10}
                      slidesPerView="auto"
                      slidesPerGroup={3}
                    >
                      {filteredFrames.map((bg, index) => (
                        <SwiperSlide key={index}>
                          <button
                            onClick={() => dispatch(setSelectedFrame(bg))}
                            className={`bg-buttons w-full rounded-10 overflow-hidden border-2 ${selectedFrame.url === bg.url ? "selected" : ""}`}
                          >
                            <img src={bg.url} alt={`Frame ${index + 1}`} className="w-full h-full object-cover" />
                          </button>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    <div className="frames-pagination" />
                  </div>

                  <div className='flex justify-center mt-3'>
                    <button onClick={() => setFramesModalActive(true)} className="frames-modal-btn">
                      Browse All
                    </button>
                  </div>

                  <FramesModal
                    isOpen={isFramesModalActive}
                    onClose={() => setFramesModalActive(false)}
                  />

                </div>
              </div>
            </div>

            <div className="flex gap-3 justify-between items-center" data-ignore-image-deselect>

              <Tooltip className="controls-tooltip" opacity="1" id="download-result" content="Download Result" place="top" />
              <Button
                onClick={handleLabelSubmit}
                type="button"
                variant="filled"
                className="justify-between"
                disabled={images.length === 0 || submitInProgress}
              >
                {!submitInProgress ?
                  <>Submit<ArrowRight size={30} /></>
                  :
                  <>Submitting<ArrowRight size={0} /></>
                }
              </Button>
            </div>

            {submitError.length > 0 && (
              <div className="text-red-600 text-md mt-2 text-center">
                {submitError.map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
              </div>
            )}

          </div>

          {/* Canvas area */}
          <div className="customizer-content">
            <div
              ref={canvasWrapperRef}
              className="relative border-3 border-dashed rounded-20 flex items-center justify-center overflow-hidden"
              style={{
                width: '100%',
                aspectRatio: '3 / 4',
                borderColor: 'var(--purple)',
                cursor: isDragging ? 'grabbing' : 'pointer'
              }}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onMouseDown={pointerDownHandler}
              onMouseMove={pointerMoveHandler}
              onMouseUp={pointerUpHandler}
              onTouchStart={pointerDownHandler}
              onTouchMove={pointerMoveHandler}
              onTouchEnd={pointerUpHandler}
            >
              <canvas
                ref={canvasRef}
                style={{ width: '100%', height: '100%' }}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
              />
              <canvas
                ref={overlayRef}
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  zIndex: 4,
                  pointerEvents: 'none',
                }}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
              />

              {!images.length && (
                <div className="absolute inset-0 flex flex-col items-center justify-center upload-container">
                  <img src={upload} alt="" />
                  <p className="text-black">
                    Drag your pet’s photo here<br />
                    and start personalizing!
                  </p>

                  <Button
                    onClick={() => document.getElementById('fileInput').click()}
                    type="button"
                    variant="filled"
                    color="secondary"
                    className="fit-width"
                  >
                    Or Select Image
                  </Button>
                </div>
              )}

            </div>
          </div>
        </div>
        <input
          id="fileInput"
          type="file"
          accept="image/png, image/jpeg, image/webp"
          className="hidden"
          onChange={handleFileSelect}
        />

      </div>
    </div>
  );
};

export default RestoreOrderLabel;
